import React, { useEffect, useState } from 'react';
import { Badge, Button, Checkbox, Col, Collapse, Form, message, Modal, Row, Space, Spin } from 'antd';
import { Permission, PermissionsList, Role } from '../../../models';
import { apiClient, apiClient2, apiErrorMessage } from '../../../utils';
import { useForm } from 'antd/lib/form/Form';
import { RolePermissionRulesPage } from './role-permission-rules-page';

export function RolePermissionsForm(props: { role?: Role }) {
  const { Panel } = Collapse;

  const [role, setRole] = useState(props.role);
  const [permissions, setPermissions] = useState<PermissionsList>();
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rulesModalOpen, setRulesModalOpen] = useState<Permission>();
  const [form] = useForm();

  const [permissionKeys, setPermissionKeys] = useState<string[]>();
  const [permissionCounts, setPermissionCounts] = useState<any>();

  useEffect(() => {
    const keys = role?.permissions.map(p => p.key);
    setPermissionKeys(keys);
    form.setFieldsValue({ permissions: keys });
  }, [form, role?.permissions]);

  useEffect(() => {
    const systemPermissionPrefixes = [
      'tasks',
      'systemSettings',
      'users',
      'roles',
      'delegations',
      'integrations',
      'tables',
      'fields',
      'reports',
      'permissions',
    ];
    const counts: any = {};
    counts['system'] =
      permissionKeys?.filter(k => systemPermissionPrefixes.includes(k.split('.')[0]) && k.split('.').length === 2).length || 0;
    counts['integrations'] = permissionKeys?.filter(k => k.split('.')[0] === 'integrations' && k.split('.').length === 3).length || 0;

    if (permissions?.gridPermissions) {
      for (const gridPermissions of permissions?.gridPermissions) {
        const key = gridPermissions.key;
        let count = gridPermissions.permissions.filter(p => permissionKeys?.some(k => k === p.key))?.length || 0;
        for (const fieldPermissions of gridPermissions.fieldPermissions) {
          count += fieldPermissions.permissions.filter(p => permissionKeys?.some(k => k === p.key))?.length || 0;
        }
        counts[key] = count;
      }
    }

    if (permissions?.subGridPermissions) {
      for (const subGridPermissions of permissions?.subGridPermissions) {
        const key = subGridPermissions.key;
        let count = subGridPermissions.permissions.filter(p => permissionKeys?.some(k => k === p.key))?.length || 0;
        for (const fieldPermissions of subGridPermissions.fieldPermissions) {
          count += fieldPermissions.permissions.filter(p => permissionKeys?.some(k => k === p.key))?.length || 0;
        }
        counts[key] = count;
      }
    }

    setPermissionCounts(counts);
  }, [permissionKeys, permissions?.gridPermissions, permissions?.subGridPermissions]);

  useEffect(() => {
    setPermissionsLoading(true);
    apiClient2
      .get('permissions')
      .then((response: any) => {
        setPermissions(response.data);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setPermissionsLoading(false);
      });
  }, []);

  const onFormChange = () => {
    const formValues = form.getFieldsValue();
    setPermissionKeys(formValues?.permissions);
  };

  const onEditFinish = (values: any) => {
    setLoading(true);
    const body = {
      permissions: values.permissions,
    };
    apiClient
      .post(`roles/${role?.id}/permissions`, body)
      .then((response: any) => {
        setRole(response.data);
        setLoading(false);
        message.success('Yetkiler başarıyla kaydedildi.');
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  };

  const openRulesModal = (permission: Permission) => {
    setRulesModalOpen(permission);
  };

  const closeRulesModal = () => {
    setRulesModalOpen(undefined);
  };

  const onRulesSaved = (role: Role) => {
    setRole(role);
    setRulesModalOpen(undefined);
  };

  return (
    <>
      {permissionsLoading && (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <Spin spinning={permissionsLoading} />
        </div>
      )}
      {permissions && (
        <Form form={form} onFinish={onEditFinish} onChange={onFormChange}>
          <Form.Item name="permissions">
            <Checkbox.Group style={{ width: '100%' }}>
              {role?.name !== 'Misafir' && (
                <Collapse>
                  <Panel
                    header={<PanelHeader title="Sistem yetkileri" count={permissionCounts['system']} />}
                    key="system"
                    forceRender={true}
                  >
                    {permissions.systemPermissions.map((sp, i) => {
                      return (
                        <Row key={sp.key} style={{ marginBottom: i + 1 !== permissions.systemPermissions.length ? '3rem' : '0' }}>
                          <Col span={6}>
                            <span>{sp.name}</span>
                          </Col>
                          <Col span={18}>
                            <Space direction="vertical">
                              {sp.permissions.map(p => {
                                return (
                                  <Checkbox key={p.key} value={p.key} style={{ display: 'block', color: '#555555' }}>
                                    {p.name}
                                  </Checkbox>
                                );
                              })}
                            </Space>
                          </Col>
                        </Row>
                      );
                    })}
                  </Panel>
                </Collapse>
              )}
              <Collapse activeKey={'mainGrids'} style={{ marginTop: '1rem' }}>
                <Panel header="Ana tablolar" key="mainGrids">
                  <Collapse style={{ marginTop: '1rem' }}>
                    {permissions.gridPermissions.map(gp => {
                      return (
                        <Panel
                          header={<PanelHeader title={gp.name + ' yetkileri'} count={permissionCounts[gp.key]} />}
                          key={gp.name}
                          forceRender={true}
                        >
                          {((gp.permissions.some(p => p.canAssignToGuests) && role?.name === 'Misafir') || role?.name !== 'Misafir') && (
                            <Row style={{ marginBottom: permissions.gridPermissions?.length ? '3rem' : '0' }}>
                              <Col span={6}>
                                <span>Genel</span>
                              </Col>
                              <Col span={18}>
                                <Space direction="vertical">
                                  {gp.permissions.map(p => {
                                    if ((p.canAssignToGuests && role?.name === 'Misafir') || role?.name !== 'Misafir') {
                                      return (
                                        <Space>
                                          <Checkbox key={p.key} value={p.key} style={{ display: 'block', color: '#555555' }}>
                                            {p.name}
                                          </Checkbox>
                                          {p.isRuleBased && role?.permissions?.some(rp => rp.key === p.key) && (
                                            <Button type="link" size="small" onClick={() => openRulesModal(p)}>
                                              Kurallar ({role?.permissions?.find(rp => rp.key === p.key)?.rule?.length || '0'})
                                            </Button>
                                          )}
                                        </Space>
                                      );
                                    }
                                    return null;
                                  })}
                                </Space>
                              </Col>
                            </Row>
                          )}
                          {gp.fieldPermissions.map((fp, i) => {
                            if ((fp.permissions.some(p => p.canAssignToGuests) && role?.name === 'Misafir') || role?.name !== 'Misafir') {
                              return (
                                <Row key={fp.key} style={{ marginBottom: i + 1 !== gp.fieldPermissions.length ? '3rem' : '0' }}>
                                  <Col span={6}>
                                    <span>{fp.name}</span>
                                  </Col>
                                  <Col span={18}>
                                    <Space direction="vertical">
                                      {fp.permissions.map(p => {
                                        if ((p.canAssignToGuests && role?.name === 'Misafir') || role?.name !== 'Misafir') {
                                          return (
                                            <Space>
                                              <Checkbox key={p.key} value={p.key} style={{ display: 'block', color: '#555555' }}>
                                                {p.name}
                                              </Checkbox>
                                              {p.isRuleBased && role?.permissions?.some(rp => rp.key === p.key) && (
                                                <Button type="link" size="small" onClick={() => openRulesModal(p)}>
                                                  Kurallar ({role?.permissions?.find(rp => rp.key === p.key)?.rule?.length || '0'})
                                                </Button>
                                              )}
                                            </Space>
                                          );
                                        }
                                        return null;
                                      })}
                                    </Space>
                                  </Col>
                                </Row>
                              );
                            }
                            return null;
                          })}
                        </Panel>
                      );
                    })}
                  </Collapse>
                </Panel>
              </Collapse>
              <Collapse activeKey={'subGrids'} style={{ marginTop: '1rem' }}>
                <Panel header="Alt tablolar" key="subGrids">
                  <Collapse style={{ marginTop: '1rem' }}>
                    {permissions.subGridPermissions.map(sgp => {
                      return (
                        <Panel
                          header={<PanelHeader title={sgp.name + ' yetkileri'} count={permissionCounts[sgp.key]} />}
                          key={sgp.name}
                          forceRender={true}
                        >
                          {((sgp.permissions.some(p => p.canAssignToGuests) && role?.name === 'Misafir') || role?.name !== 'Misafir') && (
                            <Row style={{ marginBottom: permissions.subGridPermissions?.length ? '3rem' : '0' }}>
                              <Col span={6}>
                                <span>Genel</span>
                              </Col>
                              <Col span={18}>
                                <Space direction="vertical">
                                  {sgp.permissions.map(p => {
                                    if ((p.canAssignToGuests && role?.name === 'Misafir') || role?.name !== 'Misafir') {
                                      return (
                                        <Space>
                                          <Checkbox key={p.key} value={p.key} style={{ display: 'block', color: '#555555' }}>
                                            {p.name}
                                          </Checkbox>
                                          {p.isRuleBased && role?.permissions?.some(rp => rp.key === p.key) && (
                                            <Button type="link" size="small" onClick={() => openRulesModal(p)}>
                                              Kurallar ({role?.permissions?.find(rp => rp.key === p.key)?.rule?.length || '0'})
                                            </Button>
                                          )}
                                        </Space>
                                      );
                                    }
                                    return null;
                                  })}
                                </Space>
                              </Col>
                            </Row>
                          )}
                          {sgp.fieldPermissions.map((fp, i) => {
                            if ((fp.permissions.some(p => p.canAssignToGuests) && role?.name === 'Misafir') || role?.name !== 'Misafir') {
                              return (
                                <Row key={fp.key} style={{ marginBottom: i + 1 !== sgp.fieldPermissions.length ? '3rem' : '0' }}>
                                  <Col span={6}>
                                    <span>{fp.name}</span>
                                  </Col>
                                  <Col span={18}>
                                    <Space direction="vertical">
                                      {fp.permissions.map(p => {
                                        if ((p.canAssignToGuests && role?.name === 'Misafir') || role?.name !== 'Misafir') {
                                          return (
                                            <Space>
                                              <Checkbox key={p.key} value={p.key} style={{ display: 'block', color: '#555555' }}>
                                                {p.name}
                                              </Checkbox>
                                              {p.isRuleBased && role?.permissions?.some(rp => rp.key === p.key) && (
                                                <Button type="link" size="small" onClick={() => openRulesModal(p)}>
                                                  Kurallar ({role?.permissions?.find(rp => rp.key === p.key)?.rule?.length || '0'})
                                                </Button>
                                              )}
                                            </Space>
                                          );
                                        }
                                        return null;
                                      })}
                                    </Space>
                                  </Col>
                                </Row>
                              );
                            }
                            return null;
                          })}
                        </Panel>
                      );
                    })}
                  </Collapse>
                </Panel>
              </Collapse>
              {role?.name !== 'Misafir' && (
                <Collapse style={{ marginTop: '1rem' }}>
                  <Panel
                    header={<PanelHeader title="Entegrasyon yetkileri" count={permissionCounts['integrations']} />}
                    key="integrations"
                    forceRender={true}
                  >
                    {permissions.integrationPermissions.map((fgp, i) => {
                      return (
                        <Row key={fgp.key} style={{ marginBottom: i + 1 !== permissions.integrationPermissions.length ? '3rem' : '0' }}>
                          <Col span={6}>
                            <span>{fgp.name}</span>
                          </Col>
                          <Col span={18}>
                            <Space direction="vertical">
                              {fgp.permissions.map(p => {
                                return (
                                  <Space>
                                    <Checkbox key={p.key} value={p.key} style={{ display: 'block', color: '#555555' }}>
                                      {p.name}
                                    </Checkbox>
                                    {p.isRuleBased && role?.permissions?.some(rp => rp.key === p.key) && (
                                      <Button type="link" size="small" onClick={() => openRulesModal(p)}>
                                        Kurallar ({role?.permissions?.find(rp => rp.key === p.key)?.rule?.length || '0'})
                                      </Button>
                                    )}
                                  </Space>
                                );
                              })}
                            </Space>
                          </Col>
                        </Row>
                      );
                    })}
                  </Panel>
                </Collapse>
              )}
            </Checkbox.Group>
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              Kaydet
            </Button>
          </Form.Item>
        </Form>
      )}
      <Modal
        title="Yetki kuralları"
        visible={rulesModalOpen !== undefined}
        width="90%"
        footer={null}
        onCancel={closeRulesModal}
        destroyOnClose={true}
      >
        <RolePermissionRulesPage role={role} permission={rulesModalOpen} onSaved={onRulesSaved} />
      </Modal>
    </>
  );
}

function PanelHeader(props: { title: string; count: number }) {
  return (
    <div style={{ display: 'flex' }}>
      <span style={{ marginRight: '1rem' }}>{props.title}</span>
      <Badge style={{ backgroundColor: '#108ee9' }} count={props.count} />
    </div>
  );
}
